import React, { useState } from 'react';
import './Workstation.css';
import { limitFor } from './ariaLimits';
import LessonPlayer from './LessonPlayer';
import ProjectSheet from './ProjectSheet';
import ScenePlayer, { hasScene } from './ScenePlayer';
import Showroom from './Showroom';
import { iconFor } from './sceneIcons';

// Workstation — WHERE THE WORK IS SHOWN. 2026-08-22.
//
// Braeden: "i want to make the search engine like a work station ... thats
// where visuals go, plans, blueprints, paperwork, what ever is a visual needs
// to go there ... its just where all of the info being worked on lives"
// and: "she is a great communicator, who uses her assets, visuals for
// teaching, autimation for actions, and the search engine for finding info."
//
// THE ONE RULE, learned from the version that was deleted. There WAS a
// workstation in this app; the Build/Make/Generate cards routed to it and it
// was ripped out on 2026-06-05 with the comment "no more dead workstation
// routes." It failed by being a destination with nothing in it.
//
// So this component NEVER RENDERS AN EMPTY STATE. No "nothing here yet", no
// placeholder, no frame waiting to be filled. If there is no artifact, it
// returns null and the layout closes around it. A workstation earns its space
// by having work in it.
//
// It is ADAPTIVE: the shape of the payload picks the rendering. The backend
// already returns all of these — until now the UI threw every one away.
//
//   dealMath      -> the deal sheet: every number, and the assumptions under it
//   dataPlan      -> the checklist: what to go find, how, cost, time, done-test
//   verified      -> the source panel: the fact, and where it came from
//   searchResult  -> a results page (our brand, not Google's)
//   guide         -> the system: niche, steps, and what is not verified yet
//   gap           -> what she does not know, stated plainly
//
// Blueprints, live documents and websites are the next renderers; each needs
// real data behind it, which is why they are not faked here.

const money = (n) => {
  const v = Math.round(Number(n) || 0);
  return (v < 0 ? '-$' : '$') + Math.abs(v).toLocaleString('en-US');
};

function Row({ label, value, strong, sub }) {
  return (
    <div className={'ws-row' + (strong ? ' ws-row-strong' : '')}>
      <span className="ws-row-label">{label}</span>
      <span className="ws-row-value">{value}{sub ? <span className="ws-row-sub">{sub}</span> : null}</span>
    </div>
  );
}

// ── the deal sheet ─────────────────────────────────────────────────────────
function DealSheet({ d }) {
  const isFlip = d.strategy === 'flip';
  const isBrrrr = d.strategy === 'brrrr';
  const isSpec = d.strategy === 'spec';
  return (
    <div className="ws-card">
      <div className="ws-kicker">{isFlip ? 'Flip' : isBrrrr ? 'BRRRR' : 'Spec build'} · deal sheet</div>
      <div className="ws-hero">{d.headline}</div>

      {isFlip && (
        <div className="ws-rows">
          <Row label="ARV" value={money(d.arv)} />
          <Row label={'Rehab' + (d.rehabBasis && d.rehabBasis !== 'given' ? ' (my estimate)' : '')} value={money(d.rehab)} />
          <Row label={`Margin held back (${d.marginUsed}%)`} value={money(d.arv * (d.marginUsed / 100))} />
          <Row label="Max offer" value={money(d.maxOffer)} strong />
          <Row label={`Carry · ${d.holdMonths} months`} value={money(d.carry)} />
          <Row label="Cost to sell" value={money(d.sellCosts)} />
          <Row label="All in" value={money(d.allIn)} />
          <Row label={`Profit (${d.roiOnCost}% on cost)`} value={money(d.profit)} strong />
        </div>
      )}

      {isBrrrr && (
        <div className="ws-rows">
          <Row label="Purchase" value={money(d.purchase)} />
          <Row label="Rehab" value={money(d.rehab)} />
          <Row label="All in" value={money(d.allIn)} />
          <Row label={`Refinance at ${d.ltvUsed}% of ${money(d.arv)}`} value={money(d.refiLoan)} />
          <Row label="Proceeds after costs" value={money(d.proceeds)} />
          <Row label="Cash left in" value={money(d.cashLeftIn)} strong />
          <Row label="ARV needed to pull it all back" value={money(d.arvNeededToPullAllOut)} />
          {d.dscr != null && <Row label={`DSCR (lender floor ${d.dscrFloor})`} value={d.dscr} strong={d.dscr < d.dscrFloor} />}
          {d.payment != null && <Row label="Payment / month" value={money(d.payment)} />}
        </div>
      )}

      {isSpec && (
        <div className="ws-rows">
          <Row label="Land" value={money(d.land)} />
          <Row label={`Hard cost · ${d.sqft} sqft @ ${money(d.buildPsf)}/sqft`} value={money(d.hard)} />
          <Row label="Soft costs" value={money(d.soft)} />
          <Row label={`Carry · ${d.months} months`} value={money(d.carry)} />
          <Row label="All in" value={money(d.totalCost)} />
          <Row label={`You bring (at ${d.ltcUsed}% LTC)`} value={money(d.equityNeeded)} strong />
          <Row label="Break even sale" value={money(d.breakEvenSale)} />
          <Row label="Profit" value={money(d.profit)} strong />
        </div>
      )}

      {Array.isArray(d.assumptions) && d.assumptions.length > 0 && (
        <div className="ws-note">
          <div className="ws-note-title">What I assumed</div>
          <ul>{d.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}
      {Array.isArray(d.risks) && d.risks.length > 0 && (
        <div className="ws-note ws-note-warn">
          <div className="ws-note-title">Where this breaks</div>
          <ul>{d.risks.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}
      {Array.isArray(d.financing) && d.financing.length > 0 && (
        <div className="ws-note">
          <div className="ws-note-title">How the money works</div>
          <ul>{d.financing.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// ── the checklist: what to go and find ─────────────────────────────────────
function DataPlan({ text }) {
  // The backend sends this as lines; the shape is stable enough to read back.
  const lines = String(text || '').split('\n').filter((l) => l.trim());
  const head = lines[0] || '';
  const body = lines.slice(1);
  return (
    <div className="ws-card">
      <div className="ws-kicker">What I need to answer this</div>
      <div className="ws-hero ws-hero-sm">{head}</div>
      <div className="ws-plan">
        {body.map((l, i) => {
          const isDo = /^Do this:/i.test(l);
          const isCost = /^Cost:/i.test(l);
          const isOther = /^Other ways/i.test(l);
          return (
            <div key={i} className={'ws-plan-line' + (isDo ? ' ws-plan-do' : '') + (isCost ? ' ws-plan-cost' : '') + (isOther ? ' ws-plan-alt' : '')}>
              {l}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── the source panel: this is where I got it ───────────────────────────────
function SourcePanel({ v, answer }) {
  return (
    <div className="ws-card">
      <div className="ws-kicker">Where this came from</div>
      <div className="ws-quote">{answer}</div>
      <div className="ws-rows">
        <Row label="Source" value={String(v.category || '').replace(/_/g, ' ')} />
        {v.sub && <Row label="Entry" value={v.sub} />}
        {v.searchedIn && <Row label="Searched" value={v.searchedIn} />}
        {v.score != null && <Row label="Match" value={v.score} />}
      </div>
      <div className="ws-fine">Served word for word from the library — not rewritten.</div>
    </div>
  );
}

// What a set of these is CALLED. Mirrors the `noun` on each kind in
// services/searchCategories.js — 12 kinds now, where the old inline ternary
// knew four and called a page of plumbers "listings" (8/31).
const CARD_NOUN = {
  vehicle: 'cars', home: 'places', job: 'jobs', recipe: 'recipes', product: 'items',
  local: 'businesses', restaurant: 'restaurants', lodging: 'places to stay',
  medical: 'providers', event: 'events', education: 'courses', media: 'titles',
  video: 'videos',
};

// Kinds where a missing loud field means "nobody published a rating", not
// "this thing has no price". Printing "Not listed" on a barber shop reads as
// though something is wrong with the shop; the card just shows its name and
// number instead, which is all anyone needed.
// 'video' joined them 8/31: a YouTube result has a length and a view count
// and no price at all, so "Not listed" under a video would be answering a
// question nobody asked.
const UNPRICED = new Set(['local', 'restaurant', 'medical', 'education', 'media', 'event', 'video']);

// ── ONE RESULT, WITH SOMETHING TO LOOK AT (Braeden 8/31) ──────────────────
//
// "treat it like a normal ai search bar chat, same as almost any other ai, we
//  just want to incorporate more image visuals to make it easier to read."
//
// Same complaint he made about the listing cards ("there are no pictures... to
// many words"), on the layout that fix never reached. A business search comes
// back as links, and links were a wall of blue titles and grey paragraphs.
//
// Every row now leads with a picture, in the order the web will actually give
// us one:
//
//   the page's own og:image, when it publishes one — a real photo of the
//   place, harvested server-side (services/pagePicture.js);
//   otherwise the site's favicon on a plain tile, which is what every search
//   product falls back to and is enough to tell angi from bbb at a glance.
//
// Nothing is ever invented to fill the space: a site with neither gets its
// initial, not a stock photo. The image is decoration, so it never blocks the
// row — a broken one hides itself and the text closes over it.
function ResultRow({ r, onOpen }) {
  const [shot, setShot] = useState(!!r.image);
  const [icon, setIcon] = useState(!!r.icon);
  const letter = String(r.host || r.title || '?').replace(/^www\./, '').charAt(0).toUpperCase();

  return (
    <li className="ws-result">
      <button type="button" className="ws-result-hit" onClick={() => onOpen(r.url)} title={r.url}>
        <span className="ws-result-shot" aria-hidden="true">
          {shot ? (
            <img src={r.image} alt="" loading="lazy" onError={() => setShot(false)} />
          ) : icon ? (
            <img className="ws-result-shot-ico" src={r.icon} alt="" loading="lazy" onError={() => setIcon(false)} />
          ) : (
            <span className="ws-result-letter">{letter}</span>
          )}
        </span>
        <span className="ws-result-main">
          <span className="ws-result-title">{r.title}</span>
          <span className="ws-result-host">
            {/* When the thumbnail is a real photo the favicon still earns its
                place here — it is the fastest way to read WHOSE page this is. */}
            {shot && icon && <img className="ws-result-fav" src={r.icon} alt="" loading="lazy" onError={() => setIcon(false)} />}
            {r.host}
          </span>
          {r.snippet && <span className="ws-result-snip">{r.snippet}</span>}
        </span>
      </button>
    </li>
  );
}

// ── a results page (our brand) ─────────────────────────────────────────────
// Braeden 8/22: "if i say show me realestate it needs to find me realestate
// online and show it to me." A list of what the web search found - title,
// site, one line - each opening the real page. Listed as found, never
// rewritten; the engine is named at the bottom.
// ── THE THING, NOT THE PAGE ABOUT THE THING (Braeden 8/31) ───────────────
//
// "that looks like shit, there are no pictures, no prices, to many words,
//  think about whats important on each thing looked up ... when people search
//  cars, they care about pictures, miles, price, and discription should be
//  easily findable."
//
// So the card is built in HIS reading order and nothing else gets a look in:
// the photo, then the price as the loudest thing on the card, then what it
// is, then the miles, then one line of description. No snippet paragraphs.
//
// A field the seller did not publish is simply absent — a card with no photo
// gets the site's name in the photo's place, never a stock image, and a card
// with no price says so rather than showing a zero.
// ── ONE PRODUCT, EVERY PLACE THAT SELLS IT (Braeden 9/6) ──────────────────
//
//   "there is a picture of the product, than below it is a price $1-5 with a
//    drop down of different places found, like facebook, amazon, or what ever
//    for that product that way people can choose what they want to do"
//
// So the same fan on four sites is ONE card - picture, the spread from
// cheapest to dearest, and the sellers underneath. She does not pick the shop;
// the person does. Grouping happens in services/ariaBuying.groupOffers, which
// only merges on a shared model number or a strong name overlap, because
// putting two different products under one price range misrepresents both.
//
// The root used to BE the button. A <select> cannot live inside a <button>
// (nested interactive elements: the browser's own click handling breaks and
// a screen reader reads one control), so the card is a div now with the
// picture-and-title area as the button. Cards with no offers list render
// exactly as before.
function ListingCard({ c, onOpen }) {
  const offers = Array.isArray(c.offers) ? c.offers.filter((o) => o && o.url) : [];
  const shopping = offers.length > 1;
  const [open, setOpen] = useState(false);
  const openUrl = c.url || (offers[0] && offers[0].url) || '';
  return (
    <div className={shopping ? 'ws-lc ws-lc--shop' : 'ws-lc'}>
      <button type="button" className="ws-lc-main" onClick={() => onOpen(openUrl)} title={openUrl}>
      {/* THE NAME FIRST, THEN THE PICTURE (Braeden 9/6): "a title with the
          product, picture, than a small bottom part that shows a price".
          Only on a shopping card - a car or a recipe keeps the layout it
          already had, because he was describing this one. */}
      {shopping && <div className="ws-lc-name">{c.title}</div>}
      <div className="ws-lc-shot">
        {/* photo, then the site's own icon, then its name — never a stock
            image. A business that published no logo still gets a face (8/31). */}
        {/* Listing sites hotlink-block by referrer: measured 9/2, a recipe
            photo with naturalWidth 0 left a grey void with nothing in it.
            No referrer gets most of them through; the ones that still refuse
            get the site's name, never an empty tile. */}
        {c.image
          ? <img src={c.image} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer"
              onError={(e) => {
                const tile = e.currentTarget.parentElement;
                e.currentTarget.remove();
                if (tile && !tile.querySelector('.ws-lc-noshot')) {
                  const s = document.createElement('span'); s.className = 'ws-lc-noshot'; s.textContent = c.host || ''; tile.appendChild(s);
                }
              }} />
          : c.icon
            ? <img className="ws-lc-ico" src={c.icon} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            : <span className="ws-lc-noshot">{c.host}</span>}
      </div>
      <div className="ws-lc-body">
        {/* ONE LOUD FIELD, AND IT DEPENDS ON THE THING (8/31). A car is
            judged on price; a recipe is judged on reviews — "find pictures
            online with good reviews". Whatever that field is for this kind,
            it gets the gold. Nothing published = the card says so rather
            than showing a zero. */}
        {!shopping && (c.headline || c.priceText || !UNPRICED.has(c.kind)) && (
          <div className="ws-lc-price">
            {c.headline || c.priceText || 'Not listed'}
            {c.headlineNote && <span className="ws-lc-note">{c.headlineNote}</span>}
          </div>
        )}
        {!shopping && <div className="ws-lc-title">{c.title}</div>}
        {Array.isArray(c.specs) && c.specs.length > 0 && (
          <div className="ws-lc-specs">
            {c.specs.map((s) => (
              <span key={s.label} className="ws-lc-spec">
                <b>{s.value}</b>{s.unit ? ' ' + s.unit : ''}
              </span>
            ))}
          </div>
        )}
        {c.desc && <div className="ws-lc-desc">{c.desc}</div>}
        {!shopping && <div className="ws-lc-host">{c.host}</div>}
      </div>
      </button>
      {/* WHERE TO GET IT. Only when there is more than one place - a single
          seller is already the card's own link and a dropdown of one is
          furniture. Cheapest first. An offer whose price nobody published
          still gets a row: "price not listed" is a place to go and look,
          and inventing a number there is the exact thing the picture rule
          exists to prevent. */}
      {shopping && (
        <>
          {/* THE SMALL BOTTOM PART. The spread, and a way in to the rest -
              "a price $20-100, and like a show more option, so you can see
              all the different places its at". Closed by default: the price
              is the thing you scan a grid for, the list of shops is the
              thing you open when you have picked the product. */}
          <div className="ws-lc-foot">
            <span className="ws-lc-range">{c.priceText || 'No price listed'}</span>
            <button
              type="button"
              className="ws-lc-more"
              aria-expanded={open}
              onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
            >
              {open ? 'Hide' : `${offers.length} places`}
            </button>
          </div>
          {open && (
            <ul className="ws-lc-places">
              {/* Cheapest first. An offer nobody published a price for still
                  gets a row - it is somewhere they can go and look, and
                  putting a number there instead is the same lie the picture
                  rule exists to stop. */}
              {offers.map((o, i) => (
                <li key={o.host + i}>
                  <button type="button" className="ws-lc-place" onClick={(e) => { e.stopPropagation(); onOpen(o.url); }}>
                    <span className="ws-lc-place-host">{o.host.replace(/^www\./, '')}</span>
                    <span className="ws-lc-place-price">{o.priceText || 'not listed'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}


// ── THE PREP TABLE (Braeden 8/31) ─────────────────────────────────────────
//
//   "it depends on the task that the ai is trying to do, this is where deep
//    reasoning gets used, aria will call this the prep table. she wont always
//    need it but for real tasks that take logic and thinking, she needs to."
//
// What she sets out before starting a real job: what she already has, what she
// still needs from you, and what she is going to work out herself. The point
// of showing it is that you can SEE she understands the job before she starts
// asking about it — and see why the one question she asks is that question.
//
// It never appears for a lookup. Preparing to fetch a spaghetti recipe is the
// waste that makes an assistant feel slow.
function PrepTable({ p }) {
  const total = p.known.length + p.open.length + p.mine.length;
  const done = p.known.length;
  return (
    <div className="ws-card ws-card-prep">
      <div className="ws-kicker">
        Prep table · {p.firstTime ? `first ${p.kind}` : `${p.kind} #${p.priorRuns + 1}`}
      </div>
      <div className="ws-hero ws-hero-sm">{p.problem}</div>
      <div className="ws-prep-why">{p.why}</div>

      <div className="ws-prep-meter" aria-label={`${done} of ${total} settled`}>
        <div className="ws-prep-meter__fill" style={{ width: `${Math.round((100 * done) / Math.max(1, total))}%` }} />
      </div>

      {p.known.length > 0 && (
        <div className="ws-prep-group">
          <div className="ws-prep-head">Settled</div>
          {p.known.map((k) => (
            <div key={k.key} className="ws-prep-row ws-prep-row--known">
              <span className="ws-prep-tick" aria-hidden="true">&#10003;</span>
              <span className="ws-prep-label">{k.label}</span>
              <span className="ws-prep-value">{k.value}</span>
            </div>
          ))}
        </div>
      )}

      {p.open.length > 0 && (
        <div className="ws-prep-group">
          <div className="ws-prep-head">
            Need from you
            {p.carried > 0 && <span className="ws-prep-carried">{p.carried} she can guess from last time</span>}
          </div>
          {p.open.map((o, i) => (
            <div key={o.key} className={'ws-prep-row' + (i === 0 ? ' ws-prep-row--next' : '')}>
              <span className="ws-prep-tick" aria-hidden="true" />
              <span className="ws-prep-label">{o.label}</span>
              {o.last
                ? <span className="ws-prep-last">last time: {o.last}</span>
                : <span className="ws-prep-value ws-prep-value--empty">&mdash;</span>}
            </div>
          ))}
        </div>
      )}

      {p.mine.length > 0 && (
        <div className="ws-prep-group">
          <div className="ws-prep-head">Mine to do</div>
          {p.mine.map((m) => (
            <div key={m.key} className="ws-prep-row ws-prep-row--mine">
              <span className="ws-prep-tick" aria-hidden="true" />
              <span className="ws-prep-label">{m.label}</span>
            </div>
          ))}
        </div>
      )}

      {p.nextAsk && (
        <div className="ws-prep-next">
          <span className="ws-prep-next-tag">asking first</span>
          {p.nextAsk.ask}
        </div>
      )}

      {Array.isArray(p.notes) && p.notes.length > 0 && (
        <div className="ws-note">
          <div className="ws-note-title">Noted</div>
          <ul>{p.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// compact (9/2): inside the typed timeline a turn shows a handful of cards
// and a "show all" - twenty-four per turn stacked down a timeline is a wall,
// and Braeden's brief is "no paragraphs of info, keep it so simple."
function ResultsPage({ sr, hideWords, compact = false }) {
  const [showAll, setShowAll] = useState(false);
  const open = (url) => {
    try {
      if (window.electronAPI && window.electronAPI.openExternal) window.electronAPI.openExternal(url);
      else window.open(url, '_blank', 'noopener');
    } catch (_) {}
  };
  const results = Array.isArray(sr.results) ? sr.results : [];
  const cards = Array.isArray(sr.cards) ? sr.cards : [];

  // ── ONE NUMBER, LOOKED AT (Braeden 8/31) ─────────────────────────────
  //
  // "needs to look good, makesense." A forecast used to be one spoken line
  // and an empty screen — the single search every other product answers with
  // a panel. When the answer IS a number she went and fetched, it gets the
  // gold and the rest of the readings sit under it.
  if (sr.layout === 'fact' && sr.fact) {
    const f = sr.fact;
    return (
      <div className="ws-card">
        <div className="ws-kicker">{f.title}</div>
        <div className="ws-fact">
          <div className="ws-fact-value">
            {f.value}
            {f.valueNote && <span className="ws-fact-note">{f.valueNote}</span>}
          </div>
          {f.caption && <div className="ws-fact-caption">{f.caption}</div>}
        </div>
        {Array.isArray(f.rows) && f.rows.length > 0 && (
          <div className="ws-fact-rows">
            {f.rows.map((r, i) => (
              <div key={(r.k || '') + i} className={'ws-fact-row' + (r.on ? ' is-on' : '')}>
                <div className="ws-fact-k">{r.k}</div>
                <div className="ws-fact-v">{r.v}</div>
                {r.note && <div className="ws-fact-wet">{r.note}</div>}
              </div>
            ))}
          </div>
        )}
        {sr.provenance && (
          <div className="ws-fine">{sr.provenance}{sr.asOf ? ` · as of ${String(sr.asOf).slice(0, 16).replace('T', ' ')}` : ''}</div>
        )}
      </div>
    );
  }

  // ── CARS GET A SHOWROOM (Braeden 9/3) ─────────────────────────────────
  //
  //   "this is how i want people to look at cars, give it this exact design"
  //
  // A car is not a listing tile. It is a photo the size of a windscreen, a
  // name you can read across a room, a price, and three numbers that decide
  // it — with the filters a buyer actually turns sitting beside them. Only
  // vehicles: a page of plumbers or recipes stays in the grid below, where
  // it belongs.
  if (cards.length && sr.cardKind === 'vehicle') {
    return <Showroom sr={sr} cards={cards} onOpen={open} compact={compact} />;
  }

  // Cards own the surface when the search was for THINGS.
  if (cards.length) {
    const priced = cards.map((c) => c.price).filter((p) => typeof p === 'number' && p > 0);
    const lo = priced.length ? Math.min(...priced) : null;
    const hi = priced.length ? Math.max(...priced) : null;
    return (
      <div className="ws-card">
        <div className="ws-kicker">
          {cards.length} {CARD_NOUN[sr.cardKind] || 'listings'}
          {/* Where they came from belongs at the TOP. The provenance line
              says it too, but that sits under twenty-four cards. */}
          {sr.onSite ? ` · ${sr.onSite}` : ''}
          {sr.area ? ` · ${sr.area}` : ''}
          {lo != null && hi != null && hi > lo ? ` · ${money(lo)}–${money(hi)}` : ''}
        </div>
        {!hideWords && <div className="ws-query">{sr.asked || sr.query}</div>}
        <div className="ws-lc-grid">
          {(compact && !showAll ? cards.slice(0, 6) : cards).map((c, i) => <ListingCard key={(c.url || '') + i} c={c} onOpen={open} />)}
        </div>
        {compact && !showAll && cards.length > 6 && (
          <button type="button" className="ws-showall" onClick={() => setShowAll(true)}>
            Show all {cards.length}
          </button>
        )}
        {sr.provenance && (
          <div className="ws-fine">{sr.provenance}{sr.asOf ? ` · as of ${String(sr.asOf).slice(0, 16).replace('T', ' ')}` : ''}</div>
        )}
      </div>
    );
  }

  return (
    <div className="ws-card">
      <div className="ws-kicker">{sr.engine || 'HowTo'} · results{sr.area ? ` · ${sr.area}` : ''}</div>
      {!hideWords && <div className="ws-query">{sr.asked || sr.query}</div>}
      {!hideWords && sr.answer && <div className="ws-answer">{sr.answer}</div>}
      {/* ── THE ANSWER, ABOVE THE READING LIST (8/31) ───────────────────
          "population of wichita" used to be eight good links and no number,
          while wichita.gov's own snippet had it. This is that sentence,
          lifted whole with the site named — never written here, so the site
          is clickable and the claim is checkable in one move. */}
      {sr.answerBox && (
        <a
          className="ws-answerbox"
          href={sr.answerBox.url}
          onClick={(e) => { e.preventDefault(); open(sr.answerBox.url); }}
        >
          {sr.answerBox.figure && <div className="ws-answerbox-figure">{sr.answerBox.figure}</div>}
          <div className="ws-answerbox-text">{sr.answerBox.text}</div>
          <div className="ws-answerbox-host">{sr.answerBox.host}</div>
        </a>
      )}
      {results.length > 0 && (
        <ol className="ws-results">
          {results.map((r, i) => <ResultRow key={r.url || i} r={r} onOpen={open} />)}
        </ol>
      )}
      {(sr.provenance || sr.source) && (
        <div className="ws-fine">{sr.provenance || sr.source}{sr.asOf ? ` · as of ${String(sr.asOf).slice(0, 16).replace('T', ' ')}` : ''}</div>
      )}
    </div>
  );
}

// ── the system for a niche ─────────────────────────────────────────────────
function GuidePanel({ g, text }) {
  const steps = String(text || '').split('\n').filter((l) => /^\d+\./.test(l.trim()));
  return (
    <div className="ws-card">
      <div className="ws-kicker">{g.label}</div>
      <div className="ws-hero ws-hero-sm">{g.steps} steps in this system</div>
      <ol className="ws-steps">
        {steps.map((s, i) => <li key={i}>{s.replace(/^\d+\.\s*/, '')}</li>)}
      </ol>
      {g.unverifiedSteps > 0 && (
        <div className="ws-note ws-note-warn">
          <div className="ws-note-title">Not verified</div>
          <ul><li>{g.unverifiedSteps} of these have routes I have never run myself. They are options, not advice.</li></ul>
        </div>
      )}
    </div>
  );
}

// ── what she does not know ─────────────────────────────────────────────────
// A DRAFT SHE MADE WITH YOU (Braeden 8/22): "she needs to start working with
// the user in real time ... can you help me make an add". The copy goes HERE,
// not into the spoken reply - reading three lines of ad copy out loud is the
// recital problem again. She says it is up, you read it, and the next thing
// you say edits it.
function DraftPanel({ d }) {
  const from = d.from || {};
  return (
    <div className="ws-card ws-card-draft">
      <div className="ws-kicker">Draft {d.label || d.kind}</div>
      <pre className="ws-draft-body">{d.body}</pre>
      {Object.keys(from).length > 0 && (
        <div className="ws-note">
          <div className="ws-note-title">Built from what you told me</div>
          <ul>{Object.entries(from).map(([k, v]) => <li key={k}>{v}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// THE CASE FILE (Braeden 8/22): "ok problem, we need to make an add ... a
// checklist, one by one checking off everything she needs ... notes of
// important topics the user tells her". This is her thinking, made visible:
// the problem in one line, every need with a tick or not, and what she wrote
// down. It sits ABOVE whatever else is on the workstation during a job, so he
// can see she has not forgotten the platform while she is asking about the
// budget.
// ── THE PLATFORM PICKER (Braeden 8/31) ───────────────────────────────────
//
// "show brands like logos in the app giving a display with title saying so
// she can ask which platform do you want it on."
//
// Her question is the title; the tiles are the answer. One tap sends the
// platform back as if he had typed it, so the turn she spends asking is the
// same turn he can answer in. The marks are drawn inline - the artifact must
// stay self-contained, and a logo that 404s is worse than no logo.
function BrandMark({ id }) {
  if (id === 'meta') {
    return (
      <svg viewBox="0 0 48 32" className="ws-brand" aria-hidden="true">
        <circle cx="14" cy="16" r="13" fill="#1877F2" />
        <path d="M17.3 16h-2.2v8h-3.3v-8H10v-2.8h1.8v-1.7c0-2 .8-3.5 3.4-3.5h2.2v2.8h-1.4c-.7 0-1 .3-1 1v1.4h2.5L17.3 16z" fill="#fff" />
        <rect x="21" y="4" width="24" height="24" rx="7" fill="none" stroke="#E1306C" strokeWidth="2.4" />
        <circle cx="33" cy="16" r="5.4" fill="none" stroke="#E1306C" strokeWidth="2.4" />
        <circle cx="39.4" cy="9.6" r="1.6" fill="#E1306C" />
      </svg>
    );
  }
  if (id === 'google') {
    return (
      <svg viewBox="0 0 32 32" className="ws-brand" aria-hidden="true">
        <path d="M30.6 16.3c0-1-.1-2-.3-2.9H16v5.5h8.2a7 7 0 0 1-3 4.6v3.8h4.8c2.8-2.6 4.6-6.4 4.6-11z" fill="#4285F4" />
        <path d="M16 31c4.1 0 7.6-1.4 10-3.7l-4.8-3.8c-1.3.9-3.1 1.5-5.2 1.5-4 0-7.4-2.7-8.6-6.3H2.4v4C4.9 27.6 10 31 16 31z" fill="#34A853" />
        <path d="M7.4 18.7a9 9 0 0 1 0-5.7v-4H2.4a15 15 0 0 0 0 13.4l5-3.7z" fill="#FBBC05" />
        <path d="M16 6.6c2.3 0 4.3.8 5.9 2.3l4.4-4.4C23.6 2 20.1.6 16 .6 10 .6 4.9 4 2.4 9l5 4c1.2-3.7 4.6-6.4 8.6-6.4z" fill="#EA4335" />
      </svg>
    );
  }
  if (id === 'youtube') {
    return (
      <svg viewBox="0 0 44 32" className="ws-brand" aria-hidden="true">
        <rect x="2" y="4" width="40" height="24" rx="7" fill="#FF0000" />
        <path d="M18 11.2v9.6l8.4-4.8L18 11.2z" fill="#fff" />
      </svg>
    );
  }
  // Nextdoor and anything else the catalog grows: a neutral mark rather
  // than a wrong one. Never draw a brand we have not actually drawn.
  return (
    <svg viewBox="0 0 32 32" className="ws-brand" aria-hidden="true">
      <rect width="32" height="32" rx="9" fill="#8ED500" />
      <path d="M10 23V12.6c0-1.6 1.3-2.6 2.8-2.6 1 0 1.9.5 2.4 1.3l4.6 7.1v-5c0-.9.4-1.4 1.3-1.4h1.4V21c0 1.3-1 2.2-2.3 2.2-.9 0-1.6-.4-2.1-1.1l-4.7-7.3V23h-3.4z" fill="#fff" />
    </svg>
  );
}

function PickerPanel({ p, onPick }) {
  return (
    <div className="ws-card ws-card-picker">
      <div className="ws-picker-title">{p.title}</div>
      <div className="ws-picker-grid">
        {p.options.map((o) => (
          <button
            key={o.id}
            type="button"
            className="ws-picker-tile"
            onClick={() => onPick && onPick(o)}
            aria-label={o.label}
          >
            <BrandMark id={o.id} />
            <span className="ws-picker-label">{o.label}</span>
            {o.sub && <span className="ws-picker-sub">{o.sub}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function CaseFilePanel({ f }) {
  const done = f.checklist.filter((c) => c.done).length;
  return (
    <div className="ws-card ws-card-case">
      <div className="ws-kicker">Problem</div>
      <div className="ws-case-problem">{f.problem}{f.status === 'paused' ? ' — paused' : ''}</div>
      <div className="ws-case-meter" aria-label={`${done} of ${f.checklist.length} done`}>
        <div className="ws-case-meter__fill" style={{ width: `${Math.round((100 * done) / Math.max(1, f.checklist.length))}%` }} />
      </div>
      <ul className="ws-case-list">
        {f.checklist.map((c) => (
          <li key={c.key} className={c.done ? 'done' : ''}>
            <span className="ws-case-tick" aria-hidden="true">{c.done ? '✓' : ''}</span>
            <span className="ws-case-label">{c.label}</span>
            {c.done && c.value && <span className="ws-case-value">{c.value}</span>}
          </li>
        ))}
      </ul>
      {f.notes && f.notes.length > 0 && (
        <div className="ws-note">
          <div className="ws-note-title">Noted</div>
          <ul>{f.notes.slice(-5).map((n, i) => <li key={i}>{n.text}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// THE PROPERTY RECORD (Braeden 8/22): "verify it by showing it to the user ...
// show the user the INFORMATION, property, number". Every figure with the
// source it came from and the date it was fetched, and the real links - the
// county's own tax search, the map, the owner lookup. Nothing here is a
// summary of the record; it IS the record.
function RecordPanel({ r }) {
  const money = (n) => (n == null ? '—' : '$' + Math.round(Number(n)).toLocaleString('en-US'));
  const when = (iso) => { try { return iso ? new Date(iso).toLocaleDateString() : ''; } catch (_) { return ''; } };
  const open = (url) => {
    try {
      if (window.electronAPI && window.electronAPI.openExternal) window.electronAPI.openExternal(url);
      else window.open(url, '_blank', 'noopener');
    } catch (_) {}
  };
  return (
    <div className="ws-card ws-card-record">
      <div className="ws-kicker">County record</div>
      <div className="ws-record-addr">{r.address}</div>
      <div className="ws-record-owner">{r.owner}{r.ownerType ? ` · ${r.ownerType}` : ''}{r.distress ? ` · ${r.distress}` : ''}</div>
      <div className="ws-grid">
        <Row label="Back taxes owed" value={money(r.owed && r.owed.value)} sub={r.owed && r.owed.source} />
        <Row label="Worth (ARV)" value={money(r.arv && r.arv.value)} sub={r.arv ? `${r.arv.source}${r.arv.fetched ? ' · ' + when(r.arv.fetched) : ''}` : ''} />
        <Row label="Offer" value={money(r.offer && r.offer.recommended)} sub={r.offer ? `${money(r.offer.low)}–${money(r.offer.high)} · ${r.offer.how}` : ''} />
        {r.specs && (r.specs.beds || r.specs.sqft) && (
          <Row label="Size" value={[r.specs.beds ? `${r.specs.beds} bed` : null, r.specs.sqft ? `${Number(r.specs.sqft).toLocaleString()} sqft` : null, r.specs.type].filter(Boolean).join(' · ')} />
        )}
        {r.photoCheck && <Row label="Photo check" value={r.photoCheck.usable ? 'looks like a house' : 'could not confirm'} sub={r.photoCheck.reason} />}
      </div>
      <div className="ws-record-links">
        {(r.links || []).map((l) => (
          <button key={l.label} type="button" className="ws-record-link" onClick={() => open(l.url)} title={l.url}>
            {l.label}{l.paste ? ` — paste: ${l.paste}` : ''}
          </button>
        ))}
      </div>
    </div>
  );
}

function GapPanel({ gap, text }) {
  return (
    <div className="ws-card ws-card-gap">
      <div className="ws-kicker">A gap, logged</div>
      <div className="ws-hero ws-hero-sm">{text}</div>
      <div className="ws-rows">
        <Row label="Kind" value={String(gap.workClass || '').replace(/_/g, ' ')} />
        <Row label="Changes?" value={gap.mode === 'active' ? 'yes — needs a fresh look' : 'no — a constant'} />
      </div>
    </div>
  );
}

// ── tracker cards — the numbers she just said, as the gold bubbles ────────
// Same card language as the approved trackers (8/10): wide and short, small
// label top-left, big number under it, gold, dark ink. The number on the card
// is the number in her reply — ariaVisualClass extracts, never computes.
function TrackerCards({ cards }) {
  return (
    <div className="ws-card">
      <div className="ws-kicker">The numbers</div>
      <div className="ws-trackers">
        {cards.map((c, i) => (
          <div className="ws-tracker" key={`${c.label}-${i}`}>
            <div className="ws-tracker-label">{c.label}</div>
            <div className="ws-tracker-value">{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The adaptive surface. `payload` is the raw response object from
 * /api/ai/conversation. Returns null when there is nothing to show — that is
 * deliberate and load-bearing (see the header).
 */
// inTimeline (9/2): the artifact is one entry in the typed timeline, sitting
// under the answer that produced it - no close button, no standalone frame.
export default function Workstation({ payload, onClose, onPick, hideWords, inTimeline = false, sceneClock = null }) {
  if (!payload || typeof payload !== 'object') return null;

  const text = payload.reply || payload.question || '';
  let body = null;
  let kind = null;

  // The whiteboard wins over everything: when a lesson has been built for
  // this turn, the lesson IS the answer (doctrine §3.2).
  if (payload.lesson && Array.isArray(payload.lesson.steps) && payload.lesson.steps.length) {
    body = <LessonPlayer lesson={payload.lesson} />; kind = 'lesson';
  }
  else if (payload.record && payload.record.address) { body = <RecordPanel r={payload.record} />; kind = 'record'; }
  else if (payload.draft && payload.draft.body) { body = <DraftPanel d={payload.draft} />; kind = 'draft'; }
  else if (payload.picker && Array.isArray(payload.picker.options) && payload.picker.options.length) {
    body = <PickerPanel p={payload.picker} onPick={onPick} />; kind = 'picker';
  }
  else if (payload.dealMath && payload.dealMath.ok) { body = <DealSheet d={payload.dealMath} />; kind = 'deal'; }
  else if (payload.dataPlan) { body = <DataPlan text={text} />; kind = 'plan'; }
  else if (payload.verified && payload.verified.category) { body = <SourcePanel v={payload.verified} answer={text} />; kind = 'source'; }
  // Cards count as a results page. When Aria reads a site directly (Amazon,
  // YouTube) she comes back with 24 cards and NO link list — and this gate
  // only looked at `answer` and `results`, so the richest answer we produce
  // rendered as nothing at all. Measured 8/31: 24 priced Amazon cards, blank
  // screen.
  else if (payload.searchResult && (payload.searchResult.answer
    || payload.searchResult.fact
    || (Array.isArray(payload.searchResult.cards) && payload.searchResult.cards.length)
    || (Array.isArray(payload.searchResult.results) && payload.searchResult.results.length))) { body = <ResultsPage sr={payload.searchResult} hideWords={hideWords} compact={inTimeline} />; kind = 'results'; }
  else if (payload.guide && payload.guide.label) { body = <GuidePanel g={payload.guide} text={text} />; kind = 'guide'; }
  else if (payload.gap) { body = <GapPanel gap={payload.gap} text={text} />; kind = 'gap'; }
  // NB: the prep table is NOT in this either/or chain. It is the surface the
  // job is worked ON, so it sits ABOVE whatever she is doing right now (see
  // below) rather than competing with it. Measured: "help me make an ad"
  // produced the platform picker, which outranked the table and hid the very
  // thing he asked to be able to see.
  // ── the visual tag (8/12, ARIA_VISUAL_DOCTRINE.md) ────────────────────
  // ariaVisualClass tags every turn at the res.json choke point. The shapes
  // above win first — they carry richer data. The tag catches what they
  // miss: a numbers answer becomes tracker cards built ONLY from figures
  // that appear in her actual reply.
  else if (payload.visual && payload.visual.kind === 'tracker'
           && payload.visual.payload && Array.isArray(payload.visual.payload.cards)
           && payload.visual.payload.cards.length) {
    body = <TrackerCards cards={payload.visual.payload.cards} />; kind = 'tracker';
  }
  // ── THE SCENE (9/2) — motion graphics for her words ────────────────────
  // Braeden: the After-Effects look, "3 boxes with examples of the topics",
  // "bubbly numbers ... so it all flows together." The backend compiles a
  // scene from her reply (ariaVisualClass -> ariaMotionScene, code only) or
  // the director model proposes one off the turn (/api/scene). It sits LAST
  // in this chain on purpose: a results page, a deal sheet, a whiteboard
  // all carry more than a scene does. Icons are picked here from the fixed
  // set, by label, so a scene never shows a wrong picture. `sceneClock` is
  // the reading clock on a typed turn and her speech clock on a voice turn;
  // items land on the word they belong to.
  else if (hasScene(payload.scene)) {
    const sc = { ...payload.scene, items: payload.scene.items.map((it) => (it.icon ? it : { ...it, icon: iconFor(`${it.label} ${it.sub || ''}`) })) };
    body = <ScenePlayer scene={sc} text={text} clock={sceneClock || payload._sceneClock || null} />; kind = 'scene';
  }

  // ── ONE NOTEBOOK, NOT TWO (Braeden 8/31) ──────────────────────────────
  // "she only needs one notebook thing opened up." The case file used to
  // render HERE as well as in the Notepad - the same problem line and the
  // same checklist, twice on one screen, the yellow page landing on top of
  // the dark one. The Notepad is the notebook; the workstation shows the
  // thing she is working ON.
  // THE TABLE IS THE SURFACE, THE ARTIFACT SITS ON IT (Braeden 8/31).
  const prep = (payload.prep && payload.prep.problem) ? payload.prep : null;
  // ── THE PROJECT SHEET SITS ON TOP OF WHATEVER ELSE THIS TURN MADE (9/3)
  // Braeden: "its like pulling up a digital sheet, with number 1, whats the
  // video going to be about." It is the spine of the intake, so it is never
  // replaced by the turn's artifact — on the closing turn the sheet shows the
  // filled rows with her first move on the last line, and the results grid
  // that IS that move stacks underneath it.
  if (payload.sheet && Array.isArray(payload.sheet.rows) && payload.sheet.rows.length) {
    const sheetEl = <ProjectSheet sheet={payload.sheet} />;
    body = body ? <div className="ws-stack">{sheetEl}{body}</div> : sheetEl;
    if (!kind || kind === 'scene') kind = 'sheet';
  }

  if (!body && !prep) return null;   // nothing to show — do not take up the space
  if (!kind) kind = 'prep';

  // SHE SAYS WHAT SHE IS BAD AT BEFORE SHE SHOWS IT (Braeden 8/22).
  // Above the artifact, not below it — a limit you read afterwards is an
  // excuse, a limit you read first is a heads-up. Null for the things she is
  // actually good at, because a warning on everything is wallpaper.
  // ── A SITE'S OWN PRICE IS NOT AN UNVERIFIED SOURCE (8/31) ─────────────
  //
  // The `results` limit reads "This is what I could reach, not a source I've
  // verified" — right for a page of links an engine ranked, wrong sitting on
  // top of 24 Amazon products whose prices and star ratings were read off
  // Amazon itself. This file's own rule is ONLY WHERE IT IS TRUE, and a
  // hedge on everything is wallpaper.
  //
  // Same for a fact panel: it is a reading pulled from a live feed, which
  // ariaLimits already names as a thing that carries no hedge. A forecast
  // read off open-meteo does not need "I could not verify this".
  //
  // And an answer box carries its own source, named and clickable, with the
  // sentence quoted whole off that page — "not a source I've verified" over
  // wichita.gov's own published figure is a hedge about the one answer on
  // the screen that is fully checkable.
  // ── A CARD IS ALWAYS READ OFF THE SELLER'S OWN PAGE (9/3) ─────────────
  // This used to require `onSite` — the one-site case, Amazon or YouTube.
  // But a card only ever exists because listingHarvest opened the listing
  // and read the structured data the seller published on it; a multi-site
  // harvest is no less direct than a single-site one. Measured 9/3: 23 cars
  // read off truecar's own listings carried "not a source I've verified"
  // over prices and mileage taken from the seller, which is a hedge about
  // the most checkable thing on the screen.
  const _cardsRead = !!(payload.searchResult
    && Array.isArray(payload.searchResult.cards) && payload.searchResult.cards.length);
  const _readDirect = !!(payload.searchResult && (payload.searchResult.fact
    || payload.searchResult.answerBox
    || _cardsRead));
  const limit = _readDirect ? null : limitFor(kind);

  return (
    <section className={`workstation${inTimeline ? ' workstation--timeline' : ''}`} data-kind={kind} aria-label="Workstation">
      {onClose && (
        <button type="button" className="ws-close" onClick={onClose} aria-label="Close workstation">×</button>
      )}
      {limit && (
        <div className="ws-limit">
          <span className="ws-limit-weak">{limit.weak}</span>{' '}
          <span className="ws-limit-better">{limit.better}</span>
        </div>
      )}
      {prep && <PrepTable p={prep} />}
      {body}
    </section>
  );
}

/** True when Workstation would render something - the sheet that hosts it
 *  opens only then (LAYOUT.md: the workstation never renders empty). */
export function hasArtifact(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload;
  return !!((p.sheet && Array.isArray(p.sheet.rows) && p.sheet.rows.length)
    || (p.lesson && Array.isArray(p.lesson.steps) && p.lesson.steps.length)
    || (p.picker && Array.isArray(p.picker.options) && p.picker.options.length)
    || (p.record && p.record.address)
    || (p.draft && p.draft.body)
    || (p.dealMath && p.dealMath.ok)
    || p.dataPlan
    || (p.verified && p.verified.category)
    || (p.searchResult && (p.searchResult.answer
        || p.searchResult.fact
        || (Array.isArray(p.searchResult.cards) && p.searchResult.cards.length)
        || (Array.isArray(p.searchResult.results) && p.searchResult.results.length)))
    || (p.guide && p.guide.label)
    || p.gap
    || (p.prep && p.prep.problem)
    || (p.visual && p.visual.kind === 'tracker' && p.visual.payload
        && Array.isArray(p.visual.payload.cards) && p.visual.payload.cards.length)
    || hasScene(p.scene));
}

export { DealSheet, DataPlan, SourcePanel, ResultsPage, GuidePanel, GapPanel };
